import {
  User,
  Building2,
  GitFork,
  Sparkles,
  FileText,
  EyeOff,
} from 'lucide-react';
import type { AgentSkill } from '../../../../api/agent-skills';
import { getTagColor } from '../utils/tagColors';
import type { TFunction } from 'i18next';

import { useTranslation } from 'react-i18next';

interface SkillCardProps {
  skill: AgentSkill;
  isDisabledByUser: boolean;
  currentUserId?: string;
  onClick: () => void;
}

function scopeMeta(t: TFunction<'pleri/skills'>, skill: AgentSkill) {
  if (skill.isBuiltIn) {
    return {
      icon: Sparkles,
      gradient: 'from-violet-500 to-purple-600',
      label: t('pleri'),
      badge:
        'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
    };
  }
  if (skill.scope === 'tenant') {
    return {
      icon: Building2,
      gradient: 'from-blue-500 to-indigo-600',
      label: t('common:tenant'),
      badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    };
  }
  if (skill.scope === 'template') {
    return {
      icon: GitFork,
      gradient: 'from-emerald-500 to-teal-600',
      label: t('template'),
      badge:
        'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    };
  }
  return {
    icon: User,
    gradient: 'from-orange-400 to-amber-500',
    label: t('mySkill'),
    badge:
      'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  };
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) {
    return '';
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString('en', { month: 'short', day: 'numeric' });
}

export function SkillCard({
  skill,
  isDisabledByUser,
  currentUserId,
  onClick,
}: SkillCardProps) {
  const { t } = useTranslation('pleri/skills');
  const meta = scopeMeta(t, skill);
  const Icon = meta.icon;
  const tags = skill.tags ?? [];
  const isAiAssisted =
    skill.metadata?.['_createdWith'] === 'chat' ||
    skill.metadata?.['_editedWith'] === 'chat';
  const dateStr = formatDate(skill.lastUpdated ?? skill.createdAt);

  const creatorDisplay =
    skill.scope !== 'user' && skill.creatorName
      ? currentUserId && skill.createdBy === currentUserId
        ? t('you')
        : skill.creatorName
      : null;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`group relative flex h-full w-full flex-col rounded-2xl border bg-white text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg dark:bg-gray-900 ${
        isDisabledByUser
          ? 'border-gray-200 opacity-60 dark:border-gray-800'
          : 'border-gray-200 hover:border-gray-300 dark:border-gray-800 dark:hover:border-gray-700'
      }`}
    >
      {/* Top: icon + name + badges */}
      <div className="flex items-start gap-3 p-4 pb-2">
        <div
          className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${meta.gradient} shadow-sm`}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-1">
            <h3 className="text-sm leading-relaxed font-semibold text-gray-900 dark:text-gray-100">
              {skill.displayName}
            </h3>
            <div className="flex flex-shrink-0 items-center gap-1 pt-0.5">
              {isAiAssisted && <Sparkles className="h-3 w-3 text-purple-400" />}
              {isDisabledByUser && <EyeOff className="h-3 w-3 text-gray-400" />}
              {!skill.enabled && !skill.isBuiltIn && (
                <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-xs font-medium text-gray-400 dark:bg-gray-800">
                  {t('off')}
                </span>
              )}
            </div>
          </div>
          {skill.files && skill.files.length > 0 && (
            <p className="mt-0.5 flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
              <FileText className="h-3 w-3" />
              {t('fileCount', { count: skill.files.length })}
            </p>
          )}
        </div>
      </div>
      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-2">
          {tags.map((tag) => {
            const c = getTagColor(tag);
            return (
              <span
                key={tag}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${c.card}`}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}
      {/* Description — flex-1 grows to push footer down; overflow-hidden ensures line-clamp clips correctly */}
      <div className="flex-1 overflow-hidden px-4 pb-3">
        <p className="line-clamp-4 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
          {skill.description}
        </p>
      </div>
      {/* Footer */}
      <div className="mt-auto flex items-center justify-between border-t border-gray-100 px-4 py-2.5 dark:border-gray-800">
        <div className="flex items-center gap-1.5">
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold tracking-wide uppercase ${meta.badge}`}
          >
            {meta.label}
          </span>
          {creatorDisplay && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {creatorDisplay}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {dateStr && (
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {dateStr}
            </span>
          )}
          <span className="text-xs text-gray-400 opacity-0 transition-opacity group-hover:opacity-100 dark:text-gray-500">
            →
          </span>
        </div>
      </div>
    </button>
  );
}
